import { useNavigate, useSearchParams } from '@remix-run/react'
import { latLngToCell } from 'h3-js'
import 'mapbox-gl/dist/mapbox-gl.css'
import posthog from 'posthog-js'
import { useCallback, useMemo, useState } from 'react'
import {
	GeolocateControl,
	Map as MapboxMap,
	NavigationControl,
	type MapLayerMouseEvent,
	type MapRef,
	type ViewState,
	type ViewStateChangeEvent,
} from 'react-map-gl'
import { useWindowSize } from 'usehooks-ts'
import {
	FETCH_ZOOM_THRESHOLD,
	LARGE_CLUSTER_RESOLUTION,
	PLOT_RESOLUTION,
	PLOT_ZOOM_THRESHOLD,
} from '#app/consts'
import { useFetchPlots } from '#app/hooks/use-fetch-plots'
import { useStoreContext } from '#app/store'
import { Clusters } from './clusters'
import { PlacesSearch } from './places-search'
import { PlotHovered } from './plot-hovered'
import { PlotsFetched } from './plots-fetched'
import { PlotsOwned } from './plots-owned'
import { PlotsSelected } from './plots-selected'
import { Popup, type PopupContent } from './popup'

export const Map = ({
	mapRef,
	mapboxAccessToken,
}: {
	mapRef: React.RefObject<MapRef>
	mapboxAccessToken: string
}) => {
	const debugMode = useStoreContext(s => s.debugMode)
	const [searchParams] = useSearchParams()
	const screenshot = searchParams.get('screenshot')

	const setIsMapLoaded = useStoreContext(s => s.setIsMapLoaded)
	const initialViewState = useStoreContext(s => s.viewState)

	const getLoadedPlot = useStoreContext(s => s.getLoadedPlot)
	const getSelectedPlotId = useStoreContext(s => s.getSelectedPlotId)
	const addSelectedPlotId = useStoreContext(s => s.addSelectedPlotId)
	const removeSelectedPlotId = useStoreContext(s => s.removeSelectedPlotId)

	const fetchPlots = useFetchPlots()

	const navigate = useNavigate()

	const [viewState, setViewState] = useState<ViewState>(initialViewState)

	const [popupContent, setPopupContent] = useState<PopupContent | null>(null)

	const showPlots = viewState.zoom >= PLOT_ZOOM_THRESHOLD

	const [hoveredPlot, setHoveredPlot] = useState<string>()

	const onMove = useCallback(
		(e: ViewStateChangeEvent) => {
			setViewState({
				...e.viewState,
				padding: initialViewState.padding,
			})
		},
		[initialViewState.padding],
	)

	const onMoveEnd = useCallback(
		(e: ViewStateChangeEvent) => {
			if (e.viewState.zoom >= FETCH_ZOOM_THRESHOLD) {
				const center = latLngToCell(
					e.viewState.latitude,
					e.viewState.longitude,
					LARGE_CLUSTER_RESOLUTION,
				)
				fetchPlots(center)
			}
		},
		[fetchPlots],
	)

	const onMouseMove = useCallback(
		(e: MapLayerMouseEvent) => {
			if (!showPlots) {
				e.target.getCanvas().style.cursor = 'grab'

				// TODO (med) the can be used to hover the cluster circles. need to do this for each cluster size (within the zoom levels) for both owned/selected. then create a popup which shows count of plots in the cluster
				// const features = mapRef.current?.queryRenderedFeatures(e.point, {
				//   layers: smallClusterPlotIds.map((plot) => `${plot}-circle-layer`),
				// })
				// console.log(features)

				return
			}

			const plotId = latLngToCell(e.lngLat.lat, e.lngLat.lng, PLOT_RESOLUTION)
			const fetchedPlot = getLoadedPlot(plotId)

			if (fetchedPlot) {
				e.target.getCanvas().style.cursor = 'pointer'
				setHoveredPlot(undefined)
				const content = fetchedPlot.group
					? {
							latitude: e.lngLat.lat,
							longitude: e.lngLat.lng,
							handle: fetchedPlot.group.handle,
							note: fetchedPlot.group.note ?? '',
							user: fetchedPlot.user,
						}
					: {
							latitude: e.lngLat.lat,
							longitude: e.lngLat.lng,
							handle: fetchedPlot.handle,
							note: fetchedPlot.note ?? '',
							user: fetchedPlot.user,
						}
				setPopupContent(content)
				return
			} else {
				setPopupContent(null)
			}

			if (getSelectedPlotId(plotId)) {
				e.target.getCanvas().style.cursor = 'pointer'
				setHoveredPlot(undefined)
			}

			if (!getSelectedPlotId(plotId)) {
				e.target.getCanvas().style.cursor = 'grab'
				setHoveredPlot(plotId)
			}
		},
		[showPlots, getLoadedPlot, getSelectedPlotId],
	)

	const onMouseLeave = useCallback(() => {
		setPopupContent(null)
	}, [])

	const onMapClick = useCallback(
		(e: MapLayerMouseEvent) => {
			if (!showPlots) return

			const plotId = latLngToCell(e.lngLat.lat, e.lngLat.lng, PLOT_RESOLUTION)
			const plot = getLoadedPlot(plotId)
			if (plot) {
				if (plot.group) {
					navigate(`/plot/${plot.group.handle}?from=map`)
					posthog.capture('map_plot_clicked', { handle: plot.group.handle })
					return
				}

				navigate(`/plot/${plot.handle}?from=map`)
				posthog.capture('map_plot_clicked', { handle: plot.handle })
				return
			}

			// TODO (low) right click menu?

			if (getSelectedPlotId(plotId)) {
				removeSelectedPlotId(plotId)
				e.target.getCanvas().style.cursor = 'grab'

				posthog.capture('plot_deselected', { plotId: plotId })
			} else {
				addSelectedPlotId(plotId)
				e.target.getCanvas().style.cursor = 'pointer'

				posthog.capture('plot_selected', { plotId: plotId })
			}
		},
		[
			showPlots,
			getLoadedPlot,
			getSelectedPlotId,
			navigate,
			removeSelectedPlotId,
			addSelectedPlotId,
		],
	)

	const windowSize = useWindowSize()

	const padding = useMemo(() => {
		if (screenshot)
			return {
				top: 0,
				bottom: 0,
				left: 0,
				right: 0,
			}

		if (windowSize.width < 768) {
			return {
				top: 0,
				bottom: 80,
				left: 0,
				right: 0,
			}
		}

		return {
			top: 0,
			bottom: 0,
			left: 0,
			right: 320,
		}
	}, [windowSize.width, screenshot])

	if (!windowSize.width) return null

	const height = screenshot ? '100%' : '100dvh'
	const width = screenshot ? '100%' : '100dvw'

	return (
		<>
			<MapboxMap
				ref={mapRef}
				initialViewState={{ ...initialViewState, padding }}
				mapStyle="mapbox://styles/chrismoorst/clsip9wqr003901qwc0ibf02q"
				mapboxAccessToken={mapboxAccessToken}
				onMove={onMove}
				onMoveEnd={onMoveEnd}
				onMouseMove={onMouseMove}
				onMouseLeave={onMouseLeave}
				doubleClickZoom={false}
				onClick={onMapClick}
				logoPosition="bottom-left"
				attributionControl={false}
				projection={{
					name: 'globe',
				}}
				onLoad={() => {
					mapRef.current?.resize()
					setIsMapLoaded(true)
				}}
				style={{
					width,
					height,
				}}
				preserveDrawingBuffer={!!screenshot}
			>
				<NavigationControl showCompass={false} position="bottom-left" />
				<GeolocateControl position="bottom-left" />
				{!screenshot && debugMode && <PlotsFetched />}
				{showPlots && (
					<>
						<PlotsOwned />
						<PlotHovered plot={hoveredPlot} />
						<PlotsSelected viewState={viewState} />
						<Popup
							popupContent={popupContent}
							onClose={() => setPopupContent(null)}
						/>
					</>
				)}
				<Clusters viewState={viewState} />
				{/* TODO (low) dev mode clusters for fetched with fetchedAt countdown */}
			</MapboxMap>
			{!screenshot && <PlacesSearch mapRef={mapRef} viewState={viewState} />}
		</>
	)
}
